import * as React from "react";

/**
 * Icon component props
 */
export interface IconProps extends React.SVGProps<SVGSVGElement> {
  size?: number | string;
}

/**
 * ==============================================
 * HOW TO ADD A NEW ICON:
 * ==============================================
 *
 * 1. Add your SVG file to: src/assets/icons/{iconName}.svg
 *
 * 2. Copy the SVG content from the file
 *
 * 3. Add it to the iconRegistry below:
 *    iconName: `<svg>...</svg>`,
 *
 * 4. Use it anywhere in your app:
 *    <Icon name="iconName" size={24} />
 *
 * The Icon component will automatically:
 * - Replace hardcoded colors with currentColor
 * - Allow you to control color via className or style
 * - Resize based on the size prop
 * ==============================================
 */

/**
 * Icon registry - maps icon names to their SVG content
 * Add your icons here by copying the SVG content from your icon files
 */
const iconRegistry = {
  // Tick/Check icon
  tick: `<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M7.99907 1.77063L8.63188 2.42981L8.69829 2.49915L8.63188 2.56848L3.88188 7.50891L3.80962 7.58411L3.73833 7.50891L0.887741 4.54504L0.821335 4.47571L0.887741 4.40637L1.52153 3.74719L1.5938 3.672L1.66508 3.74719L3.80962 5.97766L7.85454 1.77063L7.9268 1.69543L7.99907 1.77063Z" fill="white" stroke="white" stroke-width="0.2"/>
</svg>
`,

  // Alias: check points to the same icon as tick
  check: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M10.364 15.1924L19.5564 6L20.9706 7.41421L10.364 18.0208L4 11.6569L5.41422 10.2427L10.364 15.1924Z" fill="#081416"/>
</svg>`,

  add: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
<path d="M12.9 11.0999L21 11.0997V12.8997L12.9 12.8999V21H11.1V12.8999L3.00004 12.9001L3 11.1001L11.1 11.0999L11.0999 3.00001L12.8999 3L12.9 11.0999Z" fill="#081416"/>
</svg>`,

  // Info icon
  info: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M12 22.5C6.20101 22.5 1.5 17.7989 1.5 12C1.5 6.20101 6.20101 1.5 12 1.5C17.7989 1.5 22.5 6.20101 22.5 12C22.5 17.7989 17.7989 22.5 12 22.5ZM12 20.4C16.6392 20.4 20.4 16.6392 20.4 12C20.4 7.36081 16.6392 3.6 12 3.6C7.36081 3.6 3.6 7.36081 3.6 12C3.6 16.6392 7.36081 20.4 12 20.4ZM10.95 6.75H13.05V8.85H10.95V6.75ZM10.95 10.95H13.05V17.25H10.95V10.95Z" fill="#081416"/>
</svg>
`,

  // Exclamation/Warning icon
  exclamation: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="#081416"/>
</svg>`,

  // Close/X icon
  close: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M18 6L6 18M6 6L18 18" stroke="#081416" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
  upload: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M4 19H20V12H22V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V12H4V19ZM13 9V16H11V9H6L12 3L18 9H13Z" fill="#081416"/>
</svg>
`,
  file: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M19.1111 21H4.88889C4.39797 21 4 20.5971 4 20.1V3.9C4 3.40295 4.39797 3 4.88889 3H19.1111C19.602 3 20 3.40295 20 3.9V20.1C20 20.5971 19.602 21 19.1111 21ZM18.2222 19.2V4.8H5.77778V19.2H18.2222ZM8.44444 9.3H15.5556V11.1H8.44444V9.3ZM8.44444 12.9H15.5556V14.7H8.44444V12.9Z" fill="#081416"/>
</svg>
`,
  chevronDown: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M12 13.7272L17.4445 8L19 9.63635L12 17L5 9.63635L6.55555 8L12 13.7272Z" fill="#081416"/>
</svg>
`,
  chevronUp: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M12 11.2727L6.55555 17L5 15.3637L12 8L19 15.3637L17.4445 17L12 11.2727Z" fill="#081416"/>
</svg>
`,
  chevronLeft: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M11.2727 12L17 17.4445L15.3637 19L8 12L15.3637 5L17 6.55555L11.2727 12Z" fill="#081416"/>
</svg>
`,
  chevronRight: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M13.7272 12L8 6.55555L9.63635 5L17 12L9.63635 19L8 17.4445L13.7272 12Z" fill="#081416"/>
</svg>
`,
};

/**
 * Type for available icon names (with autocomplete support)
 */
export type IconName = keyof typeof iconRegistry;

/**
 * Icon component that renders an SVG icon by name
 *
 * Features:
 * - ✓ Automatically converts hardcoded colors to currentColor
 * - ✓ Control color via className (e.g., "text-blue-500")
 * - ✓ Control color via style prop
 * - ✓ Customizable size
 * - ✓ TypeScript autocomplete for icon names
 * - ✓ All standard SVG props supported
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Icon name="tick" size={24} />
 *
 * // With Tailwind color class
 * <Icon name="tick" size={16} className="text-blue-500" />
 *
 * // With inline styles
 * <Icon name="check" size={20} style={{ color: 'red' }} />
 *
 * // With custom props
 * <Icon name="tick" size={32} className="hover:text-green-600 transition-colors" />
 * ```
 */
export interface DynamicIconProps
  extends Omit<
    React.SVGProps<SVGSVGElement>,
    "ref" | "dangerouslySetInnerHTML"
  > {
  /** Name of the icon to render */
  name: IconName;
  /** Size of the icon (width and height) */
  size?: number | string;
}

export const Icon: React.FC<DynamicIconProps> = ({
  name,
  size = 24,
  className = "",
  style = {},
  ...props
}) => {
  const svgContent = iconRegistry[name];

  if (!svgContent) {
    console.warn(
      `Icon "${String(name)}" not found in registry.\n` +
        `Available icons: ${Object.keys(iconRegistry).join(", ")}`
    );
    return null;
  }

  // Parse the SVG content
  const parser = new DOMParser();
  const svgDoc = parser.parseFromString(svgContent, "image/svg+xml");
  const svgElement = svgDoc.querySelector("svg");

  if (!svgElement) {
    console.error(`Invalid SVG content for icon "${String(name)}"`);
    return null;
  }

  // Get the viewBox for proper scaling
  const viewBox = svgElement.getAttribute("viewBox") || "0 0 24 24";

  // Get all SVG content as string and replace hardcoded colors with currentColor
  let innerSVG = svgElement.innerHTML;

  // Replace common hardcoded colors with currentColor
  // This allows the icon to inherit text color from parent
  innerSVG = innerSVG
    .replace(/fill="[^"]*"/g, 'fill="currentColor"')
    .replace(/stroke="[^"]*"/g, 'stroke="currentColor"');

  return (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      className={className}
      style={style}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
      dangerouslySetInnerHTML={{ __html: innerSVG }}
    />
  );
};

/**
 * Get all available icon names from the registry
 * @returns Array of registered icon names
 *
 * @example
 * ```tsx
 * const icons = getAvailableIcons();
 * console.log(icons); // ['tick', 'check', ...]
 * ```
 */
export function getAvailableIcons(): IconName[] {
  return Object.keys(iconRegistry) as IconName[];
}

/**
 * Check if an icon exists in the registry
 * @param name - Icon name to check
 * @returns true if the icon exists
 *
 * @example
 * ```tsx
 * if (hasIcon('tick')) {
 *   // Icon exists
 * }
 * ```
 */
export function hasIcon(name: string): name is IconName {
  return name in iconRegistry;
}

export { iconRegistry };
